import React, {FunctionComponent} from 'react';
import {
    Box,
    Chip,
    IconButton,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import HighlightText from '../../app-specific/highlight-text';
import CommandSearchResultIcon from './result-icon';
import {getCommandSearchLinkComponentProps, handleCommandSearchLinkClick} from './link-props';
import type {CommandSearchResult} from '../types';

type RememberResultFn = (
    result: CommandSearchResult,
    actionKey?: string,
    actionPayload?: Record<string, string | number | boolean | undefined>,
) => void;

interface CommandSearchResultRowProps {
    item: CommandSearchResult;
    selected: boolean;
    query: string;
    itemRef?: (element: HTMLElement | null) => void;
    onMouseEnter: () => void;
    onClick: () => void;
    onRememberResult?: RememberResultFn;
    onRemove?: () => void;
    removeLabel?: string;
    /** Closes the command palette after a row action navigates. */
    onClose?: () => void;
}

/** Rich command-palette result row with avatar, badge, meta, and query highlighting. */
const CommandSearchResultRow: FunctionComponent<CommandSearchResultRowProps> = ({
    item,
    selected,
    query,
    itemRef,
    onMouseEnter,
    onClick,
    onRememberResult,
    onRemove,
    removeLabel = 'Remove',
    onClose,
}) => {
    if (item.renderRow) {
        return (
            <Box ref={itemRef}>
                {item.renderRow({item, selected, query, onMouseEnter, onClick})}
            </Box>
        );
    }

    const hasLeadingVisual = Boolean(item.avatarUrl || item.icon);
    const rowActions = item.rowActions ?? [];
    const hasTrailingActions = Boolean(onRemove) || rowActions.length > 0;
    const linkProps = getCommandSearchLinkComponentProps({
        to: item.to,
        target: item.target,
        rel: item.rel,
    });

    return (
        <Box
            ref={itemRef}
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                mx: 0.5,
                mb: 0.25,
            }}
        >
            <ListItemButton
                selected={selected}
                onMouseEnter={onMouseEnter}
                {...linkProps}
                onClick={(event) => {
                    const {runHandler, preventDefault} = handleCommandSearchLinkClick(event, {
                        to: item.to,
                        target: item.target,
                        rel: item.rel,
                    });
                    if (preventDefault) {
                        event.preventDefault();
                    }
                    if (runHandler) {
                        onClick();
                        return;
                    }
                    // Modified / middle-click: browser follows the link; still record Recents.
                    if (item.to) {
                        onRememberResult?.(item);
                    }
                }}
                sx={{
                    flex: 1,
                    borderRadius: hasTrailingActions ? '12px 0 0 12px' : 1.5,
                    alignItems: 'flex-start',
                    py: 1.1,
                    border: 1,
                    borderColor: selected ? 'primary.main' : 'transparent',
                    borderRight: hasTrailingActions ? 0 : undefined,
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    textDecoration: 'none',
                    color: 'inherit',
                }}
            >
                {hasLeadingVisual && (
                    <ListItemIcon sx={{minWidth: 40, mt: 0.2}}>
                        <CommandSearchResultIcon icon={item.icon} avatarUrl={item.avatarUrl} size="row"/>
                    </ListItemIcon>
                )}

                <ListItemText
                    disableTypography
                    primary={(
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                            <Typography variant="body2" sx={{fontWeight: 700, lineHeight: 1.35}}>
                                <HighlightText text={item.title} query={query}/>
                            </Typography>
                            {item.badge && (
                                <Chip
                                    label={item.badge}
                                    size="small"
                                    color={item.badgeColor ?? 'default'}
                                    variant={item.badgeColor ? 'filled' : 'outlined'}
                                    sx={{height: 20, '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem', fontWeight: 700}}}
                                />
                            )}
                        </Box>
                    )}
                    secondary={item.subtitle ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mt: 0.35,
                                lineHeight: 1.4,
                            }}
                        >
                            <HighlightText text={item.subtitle} query={query}/>
                        </Typography>
                    ) : null}
                />

                {item.meta && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{flexShrink: 0, ml: 1, mt: 0.25, fontWeight: 500}}
                    >
                        {item.meta}
                    </Typography>
                )}
            </ListItemButton>
            {rowActions.map((action, index) => {
                const isLast = !onRemove && index === rowActions.length - 1;
                const actionLinkProps = getCommandSearchLinkComponentProps({
                    to: action.to,
                    target: action.target,
                    rel: action.rel,
                });
                return (
                    <IconButton
                        key={action.id}
                        size="small"
                        aria-label={action.label}
                        title={action.label}
                        onMouseEnter={onMouseEnter}
                        {...actionLinkProps}
                        onClick={(event) => {
                            event.stopPropagation();
                            const {runHandler, preventDefault} = handleCommandSearchLinkClick(event, {
                                to: action.to,
                                target: action.target,
                                rel: action.rel,
                            });
                            if (preventDefault) {
                                event.preventDefault();
                            }
                            if (!runHandler) {
                                if (action.to) {
                                    onRememberResult?.(item, 'navigate', {to: action.to});
                                }
                                return;
                            }
                            action.onClick();
                            if (action.to) {
                                onRememberResult?.(item, 'navigate', {to: action.to});
                            }
                            onClose?.();
                        }}
                        sx={{
                            alignSelf: 'stretch',
                            borderRadius: isLast ? '0 12px 12px 0' : 0,
                            border: 1,
                            borderLeft: 0,
                            borderColor: selected ? 'primary.main' : 'transparent',
                            bgcolor: selected ? 'action.selected' : 'transparent',
                            color: 'text.secondary',
                            textDecoration: 'none',
                            '&:hover': {
                                color: 'primary.main',
                                bgcolor: 'action.hover',
                            },
                        }}
                    >
                        {action.icon}
                    </IconButton>
                );
            })}
            {onRemove && (
                <IconButton
                    size="small"
                    aria-label={removeLabel}
                    title={removeLabel}
                    onMouseEnter={onMouseEnter}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemove();
                    }}
                    sx={{
                        alignSelf: 'stretch',
                        borderRadius: '0 12px 12px 0',
                        border: 1,
                        borderLeft: 0,
                        borderColor: selected ? 'primary.main' : 'transparent',
                        bgcolor: selected ? 'action.selected' : 'transparent',
                        color: 'text.secondary',
                        '&:hover': {
                            color: 'error.main',
                            bgcolor: 'action.hover',
                        },
                    }}
                >
                    <CloseRounded fontSize="small"/>
                </IconButton>
            )}
        </Box>
    );
};

export default CommandSearchResultRow;
